import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Filter,
  Package,
  Pencil,
  Trash2,
  AlertTriangle,
  X,
  Loader2,
  Palette,
  Ruler,
  Tags,
  ChevronDown,
  ChevronUp,
  Info,
  PackagePlus,
  History
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import PhotoUpload from "../components/PhotoUpload";
import ProductPhoto, { clearPhotoCache } from "../components/ProductPhoto";
import { photoStorage, getCategoryIcon } from "../lib/photoStorage";
import { EmptyStatePresets } from "../components/EmptyState";
import { isBoissonsBoutique, CONTENANCE_OPTIONS, FORMAT_OPTIONS, getVariantLabels, getFeatures } from "../lib/boutiqueType";
import { Switch } from "../components/ui/switch";
import { RestockDialog, StockHistoryDialog } from "../components/stock/StockDialogs";
import { DeletionPinDialog } from "../components/DeletionPinDialog";
import { useDeletionPin } from "../hooks/useDeletionPin";

// Fashion-specific constants
const SIZE_OPTIONS = ["XS", "S", "M", "L", "XL", "XXL", "34", "36", "38", "40", "42", "44", "46", "48", "Unique"];
const GENDER_OPTIONS = ["Femme", "Homme", "Enfant", "Mixte"];
const MAX_PRODUCTS = 50;
const COLOR_PRESETS = [
  { name: "Noir", hex: "#000000" },
  { name: "Blanc", hex: "#FFFFFF" },
  { name: "Beige", hex: "#F5F5DC" },
  { name: "Gris", hex: "#808080" },
  { name: "Marine", hex: "#000080" },
  { name: "Rouge", hex: "#DC143C" },
  { name: "Rose", hex: "#FFC0CB" },
  { name: "Bleu", hex: "#4169E1" },
  { name: "Vert", hex: "#228B22" },
  { name: "Marron", hex: "#8B4513" },
  { name: "Bordeaux", hex: "#800020" },
  { name: "Or", hex: "#FFD700" },
];

const initialFormState = {
  name: "",
  category_id: "",
  purchase_price: "",
  sale_price: "",
  gender: "Femme",
  low_stock_threshold: "3",
  // Initial variant
  size: "",
  color: "",
  color_hex: "#000000",
  quantity: "0",
  track_stock: true,
  photo: null,
  alcohol_degree: "",
  vintage: ""
};

export default function Products() {
  const { getAuthHeaders } = useAuth();
  const [searchParams] = useSearchParams();
  const boutiqueData = JSON.parse(localStorage.getItem("boutique") || "{}");
  const devise = boutiqueData.currencySymbol || "€";
  // [CAVE-BOISSONS] Type-specific options
  const boissons = isBoissonsBoutique();
  const labels = getVariantLabels();
  // [PROFIL-CAISSE] Feature flags (variants hidden for simple POS articles)
  const features = getFeatures();
  const showVariants = features.variants;
  const catLabel = features.categoryLabel;
  const sizeOptions = boissons ? CONTENANCE_OPTIONS : SIZE_OPTIONS;
  const colorOptions = boissons ? FORMAT_OPTIONS : COLOR_PRESETS;
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterGender, setFilterGender] = useState("all");
  const [filterLowStock, setFilterLowStock] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [expandedProducts, setExpandedProducts] = useState({});
  
  // Variant management
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newVariant, setNewVariant] = useState({ size: "", color: "", color_hex: "#000000", quantity: 0 });
  
  // [APPRO-STOCK] Restock management
  const [restockOpen, setRestockOpen] = useState(false);
  const [restockProduct, setRestockProduct] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`, getAuthHeaders()),
        axios.get(`${API}/categories`, getAuthHeaders())
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const lowStockParam = searchParams.get("lowStock");
    if (lowStockParam === "true") {
      setFilterLowStock(true);
    }
  }, [searchParams]);

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === "all" || product.category_id === filterCategory;
    const matchesGender = filterGender === "all" || product.gender === filterGender;
    const matchesLowStock = !filterLowStock || product.is_low_stock;
    return matchesSearch && matchesCategory && matchesGender && matchesLowStock;
  });

  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    return cat?.name || "Sans catégorie";
  };

  const handleOpenDialog = async (product = null) => {
    if (product) {
      setEditingProduct(product);
      // Priorité: image du backend (MongoDB), fallback cache local
      const photo = product.image || await photoStorage.getPhoto(product.id);
      const firstVariant = product.variants?.[0] || {};
      setFormData({
        name: product.name,
        category_id: product.category_id,
        purchase_price: product.purchase_price.toString(),
        sale_price: product.sale_price.toString(),
        gender: product.gender || "Femme",
        low_stock_threshold: product.low_stock_threshold?.toString() || "3",
        size: firstVariant.size || "",
        color: firstVariant.color || "",
        color_hex: firstVariant.color_hex || "#000000",
        quantity: firstVariant.quantity?.toString() || "0",
        track_stock: product.track_stock !== false,
        photo: photo,
        alcohol_degree: product.alcohol_degree?.toString() || "",
        vintage: product.vintage || ""
      });
    } else {
      setEditingProduct(null);
      setFormData(initialFormState);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
    setFormData(initialFormState);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.category_id || !formData.purchase_price || !formData.sale_price) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    // Validate sale_price > purchase_price
    const pp = parseFloat(formData.purchase_price);
    const sp = parseFloat(formData.sale_price);
    if (sp <= pp) {
      toast.error("Le prix de vente doit être supérieur au prix d'achat");
      return;
    }

    if (!formData.quantity || parseInt(formData.quantity) < 0) {
      toast.error("La quantité est obligatoire");
      return;
    }

    if (!formData.low_stock_threshold || parseInt(formData.low_stock_threshold) < 0) {
      toast.error("Le seuil d'alerte est obligatoire");
      return;
    }

    // Vérifier la limite de 50 produits lors de la création
    if (!editingProduct && products.length >= MAX_PRODUCTS) {
      toast.error(`Limite atteinte : ${MAX_PRODUCTS} articles maximum par boutique`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        category_id: formData.category_id,
        purchase_price: parseFloat(formData.purchase_price),
        sale_price: parseFloat(formData.sale_price),
        gender: features.gender ? formData.gender : "Mixte",
        low_stock_threshold: parseInt(formData.low_stock_threshold) || 3,
        track_stock: showVariants ? true : formData.track_stock,
        simple: !showVariants,
        size: showVariants ? (formData.size || null) : null,
        color: showVariants ? (formData.color || null) : null,
        color_hex: formData.color_hex || "#000000",
        quantity: parseInt(formData.quantity) || 0,
        alcohol_degree: formData.alcohol_degree ? parseFloat(formData.alcohol_degree) : null,
        vintage: formData.vintage || null,
        image: formData.photo || null  // Include image in payload for backend storage
      };

      let productId;
      
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload, getAuthHeaders());
        productId = editingProduct.id;
        
        // Update image in backend if changed
        if (formData.photo) {
          await axios.post(`${API}/products/${productId}/image`, { image: formData.photo }, getAuthHeaders());
        } else if (editingProduct.image) {
          // Delete image if removed
          await axios.delete(`${API}/products/${productId}/image`, getAuthHeaders());
        }
        
        toast.success("Article modifié avec succès");
      } else {
        const response = await axios.post(`${API}/products`, payload, getAuthHeaders());
        productId = response.data.id;
        toast.success("Article ajouté avec succès");
      }
      
      // Save photo to IndexedDB (local cache)
      if (formData.photo) {
        await photoStorage.savePhoto(productId, formData.photo);
        clearPhotoCache(productId);
      } else if (editingProduct) {
        await photoStorage.deletePhoto(productId);
        clearPhotoCache(productId);
      }
      
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pin) => {
    if (!productToDelete) return { ok: false };
    const headers = { ...(getAuthHeaders().headers || {}) };
    if (pin) headers["X-Deletion-Pin"] = pin;
    await axios.delete(`${API}/products/${productToDelete.id}`, { headers });
    await photoStorage.deletePhoto(productToDelete.id);
    clearPhotoCache(productToDelete.id);
    toast.success("Article supprimé");
    setDeleteDialogOpen(false);
    setProductToDelete(null);
    fetchData();
  };

  const deletionPin = useDeletionPin(handleDelete);

  const requestDelete = async () => {
    try {
      await deletionPin.runDelete();
    } catch (error) {
      const detail = error.response?.data?.detail || "Erreur lors de la suppression";
      toast.error(detail);
    }
  };

  const openAddVariant = (product) => {
    setSelectedProduct(product);
    setNewVariant({ size: "", color: "", color_hex: "#000000", quantity: 0 });
    setVariantDialogOpen(true);
  };

  const handleAddVariant = async () => {
    if (!newVariant.size || !newVariant.color) {
      toast.error(`${labels.size} et ${labels.color} sont obligatoires`);
      return;
    }
    
    try {
      await axios.post(`${API}/products/${selectedProduct.id}/variants`, newVariant, getAuthHeaders());
      toast.success("Variante ajoutée");
      setVariantDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleDeleteVariant = async (productId, variantId) => {
    try {
      await axios.delete(`${API}/products/${productId}/variants/${variantId}`, getAuthHeaders());
      toast.success("Variante supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const toggleExpand = (productId) => {
    setExpandedProducts(prev => ({ ...prev, [productId]: !prev[productId] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground font-outfit">Articles</h1>
            <span className={`text-sm font-medium px-2.5 py-0.5 rounded-full ${
              products.length >= MAX_PRODUCTS 
                ? 'bg-destructive/15 text-destructive' 
                : products.length >= MAX_PRODUCTS - 5 
                  ? 'bg-yellow-500/15 text-yellow-600' 
                  : 'bg-muted text-muted-foreground'
            }`} data-testid="product-count-badge">
              {products.length}/{MAX_PRODUCTS}
            </span>
          </div>
          <p className="text-muted-foreground">{features.productsSubtitle}</p>
        </div>
        <div className="flex items-center gap-2">
        <Button
          variant="outline"
          onClick={() => setHistoryOpen(true)}
          data-testid="stock-history-btn"
        >
          <History className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Historique stock</span>
          <span className="sm:hidden">Stock</span>
        </Button>
        <Button 
          onClick={() => {
            if (products.length >= MAX_PRODUCTS) {
              toast.error(`Limite atteinte : ${MAX_PRODUCTS} articles maximum par boutique`);
              return;
            }
            handleOpenDialog();
          }} 
          className="btn-primary" 
          data-testid="add-product-btn"
          disabled={products.length >= MAX_PRODUCTS}
        >
          <Plus className="w-5 h-5 mr-2" />
          Nouvel article
        </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un article..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-full sm:w-48" data-testid="filter-category">
            <SelectValue placeholder={catLabel} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les {catLabel.toLowerCase()}s</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {features.gender && (
        <Select value={filterGender} onValueChange={setFilterGender}>
          <SelectTrigger className="w-full sm:w-36" data-testid="filter-gender">
            <SelectValue placeholder="Genre" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            {GENDER_OPTIONS.map((g) => (
              <SelectItem key={g} value={g}>{g}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        )}
        <Button
          variant={filterLowStock ? "default" : "outline"}
          onClick={() => setFilterLowStock(!filterLowStock)}
          className={filterLowStock ? "bg-destructive hover:bg-destructive/90" : ""}
          data-testid="filter-low-stock"
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          Stock bas
        </Button>
      </div>

      {/* Products List */}
      {filteredProducts.length === 0 ? (
        products.length === 0 ? (
          <EmptyStatePresets.NoProducts onAction={() => handleOpenDialog()} />
        ) : (
          <EmptyStatePresets.NoSearchResults searchTerm={searchTerm} />
        )
      ) : (
        <div className="space-y-3">
          {filteredProducts.map((product) => (
            <Card key={product.id} className="card-product overflow-hidden" data-testid={`product-${product.id}`}>
              <Collapsible open={expandedProducts[product.id]} onOpenChange={() => toggleExpand(product.id)}>
                <div className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Photo */}
                    <ProductPhoto
                      productId={product.id}
                      backendImage={product.image}
                      categoryName={product.category_name}
                      categoryIcon={product.category_icon}
                      size="md"
                    />
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold truncate">{product.name}</h3>
                        {features.gender && <Badge variant="outline" className="text-xs">{product.gender}</Badge>}
                        {boissons && product.alcohol_degree != null && (
                          <Badge variant="outline" className="text-xs">{product.alcohol_degree}% vol</Badge>
                        )}
                        {boissons && product.vintage && (
                          <Badge variant="outline" className="text-xs">{product.vintage}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{product.category_icon} {product.category_name}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-lg font-bold">{product.sale_price.toFixed(2)} {devise}</span>
                        {product.track_stock === false ? (
                          <Badge variant="outline" className="text-muted-foreground" data-testid={`untracked-badge-${product.id}`}>Stock non suivi</Badge>
                        ) : (
                          <Badge className={product.is_low_stock ? "bg-destructive" : "bg-green-600"}>
                            {product.total_quantity} en stock
                          </Badge>
                        )}
                        {showVariants && (
                          <Badge variant="secondary">
                            {product.variants?.length || 0} variante(s)
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {showVariants && (
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`expand-${product.id}`}>
                          {expandedProducts[product.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </CollapsibleTrigger>
                      )}
                      {product.track_stock !== false && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setRestockProduct(product); setRestockOpen(true); }}
                        className="text-green-600 hover:text-green-700"
                        title="Approvisionner"
                        data-testid={`restock-btn-${product.id}`}
                      >
                        <PackagePlus className="w-4 h-4" />
                      </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(product)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setProductToDelete(product); setDeleteDialogOpen(true); }}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Variants section */}
                {showVariants && (
                <CollapsibleContent>
                  <div className="border-t bg-secondary/30 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-sm">{labels.variantsTitle}</h4>
                      <Button size="sm" variant="outline" onClick={() => openAddVariant(product)}>
                        <Plus className="w-4 h-4 mr-1" /> Ajouter
                      </Button>
                    </div>
                    {product.variants?.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {product.variants.map((variant) => (
                          <div
                            key={variant.id}
                            className="flex items-center justify-between p-2 bg-card rounded-lg border"
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-4 h-4 rounded-full border"
                                style={{ backgroundColor: variant.color_hex }}
                              />
                              <span className="text-sm font-medium">{variant.size}</span>
                              <span className="text-xs text-muted-foreground">{variant.color}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">{variant.quantity}</Badge>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-destructive"
                                onClick={() => handleDeleteVariant(product.id, variant.id)}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        {boissons ? "Aucune variante. Ajoutez des contenances et formats." : "Aucune variante. Ajoutez des tailles et couleurs."}
                      </p>
                    )}
                  </div>
                </CollapsibleContent>
                )}
              </Collapsible>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">
              {editingProduct ? "Modifier l'article" : "Nouvel article"}
            </DialogTitle>
            <DialogDescription>
              {editingProduct ? "Modifiez les informations de l'article" : showVariants ? "Ajoutez un nouvel article à votre collection" : "Photo, nom, prix et quantité — c'est tout."}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Photo */}
            <div className="flex justify-center">
              <PhotoUpload
                value={formData.photo}
                onChange={(photo) => setFormData({...formData, photo})}
                categoryIcon={getCategoryIcon(categories.find(c => c.id === formData.category_id)?.name)}
                size="large"
              />
            </div>

            {/* Basic info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="name">Nom de l'article *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder={boissons ? "Ex: Vin rouge Bordeaux" : showVariants ? "Ex: Robe fleurie été" : "Ex: Poulet braisé"}
                  data-testid="product-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>{catLabel} *</Label>
                <Select value={formData.category_id} onValueChange={(value) => setFormData({...formData, category_id: value})}>
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue placeholder="Choisir..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.icon} {cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {features.gender && (
              <div className="space-y-2">
                <Label>Genre *</Label>
                <Select value={formData.gender} onValueChange={(value) => setFormData({...formData, gender: value})}>
                  <SelectTrigger data-testid="product-gender-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDER_OPTIONS.map((g) => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}
            </div>

            {/* [CAVE-BOISSONS] Champs spécifiques boissons */}
            {boissons && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Degré d'alcool (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.alcohol_degree}
                    onChange={(e) => setFormData({...formData, alcohol_degree: e.target.value})}
                    placeholder="Ex: 12.5 (optionnel)"
                    data-testid="product-alcohol-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Millésime</Label>
                  <Input
                    value={formData.vintage}
                    onChange={(e) => setFormData({...formData, vintage: e.target.value})}
                    placeholder="Ex: 2019 (optionnel)"
                    data-testid="product-vintage-input"
                  />
                </div>
              </div>
            )}

            {/* Prices */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prix d'achat ({devise}) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.purchase_price}
                  onChange={(e) => setFormData({...formData, purchase_price: e.target.value})}
                  placeholder="0.00"
                  data-testid="product-purchase-price"
                />
              </div>
              <div className="space-y-2">
                <Label>Prix de vente ({devise}) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.sale_price}
                  onChange={(e) => setFormData({...formData, sale_price: e.target.value})}
                  placeholder="0.00"
                  data-testid="product-sale-price"
                  style={
                    formData.purchase_price && formData.sale_price && parseFloat(formData.sale_price) <= parseFloat(formData.purchase_price)
                      ? { borderColor: '#ef4444' } : {}
                  }
                />
              </div>
            </div>

            {/* Price validation error + real-time margin */}
            {formData.purchase_price && formData.sale_price && (() => {
              const pp = parseFloat(formData.purchase_price) || 0;
              const sp = parseFloat(formData.sale_price) || 0;
              const margin = sp - pp;
              const marginPct = pp > 0 ? ((margin / sp) * 100).toFixed(1) : 0;
              const isNeg = sp <= pp;
              return (
                <div className="flex items-center justify-between" data-testid="margin-indicator">
                  {isNeg ? (
                    <p className="text-sm font-medium" style={{ color: '#ef4444' }}>
                      Le prix de vente doit être supérieur au prix d'achat
                    </p>
                  ) : (
                    <p className="text-sm font-medium" style={{ color: '#16a34a' }}>
                      Marge estimée : {margin.toLocaleString('fr-FR')} {devise} ({marginPct}%)
                    </p>
                  )}
                </div>
              );
            })()}

            {/* [PROFIL-CAISSE] Article simple : quantité + seuil + suivi du stock */}
            {!showVariants && (
              <div className="border-t pt-4 mt-4 space-y-4" data-testid="simple-stock-section">
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <Label htmlFor="track-stock-switch" className="font-medium">Suivre le stock</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Désactivez pour les plats cuisinés ou services vendus sans limite.
                    </p>
                  </div>
                  <Switch
                    id="track-stock-switch"
                    checked={formData.track_stock}
                    onCheckedChange={(v) => setFormData({...formData, track_stock: v})}
                    data-testid="track-stock-switch"
                  />
                </div>
                {formData.track_stock && (
                  <div className="grid grid-cols-2 gap-3">
                    {!editingProduct && (
                    <div className="space-y-2">
                      <Label>Quantité en stock *</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.quantity}
                        onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                        placeholder="0"
                        data-testid="simple-quantity-input"
                      />
                    </div>
                    )}
                    <div className="space-y-2">
                      <Label>Seuil d'alerte *</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.low_stock_threshold}
                        onChange={(e) => setFormData({...formData, low_stock_threshold: e.target.value})}
                        placeholder="3"
                        data-testid="simple-threshold-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Initial variant (only for new products) */}
            {showVariants && !editingProduct && (
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Ruler className="w-4 h-4" />
                  Première variante (optionnel)
                </h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-2">
                    <Label>{labels.size}</Label>
                    <Select value={formData.size} onValueChange={(value) => setFormData({...formData, size: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder={labels.size} />
                      </SelectTrigger>
                      <SelectContent>
                        {sizeOptions.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{labels.color}</Label>
                    <Select value={formData.color} onValueChange={(value) => {
                      const preset = colorOptions.find(c => c.name === value);
                      setFormData({...formData, color: value, color_hex: preset?.hex || formData.color_hex});
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder={labels.color} />
                      </SelectTrigger>
                      <SelectContent>
                        {colorOptions.map((c) => (
                          <SelectItem key={c.name} value={c.name}>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full border" style={{ backgroundColor: c.hex }} />
                              {c.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Quantité *</Label>
                    <Input
                      type="number"
                      min="0"
                      required
                      value={formData.quantity}
                      onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Seuil alerte *</Label>
                    <Input
                      type="number"
                      min="0"
                      required
                      value={formData.low_stock_threshold}
                      onChange={(e) => setFormData({...formData, low_stock_threshold: e.target.value})}
                      placeholder="3"
                    />
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Annuler
              </Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-product-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingProduct ? "Modifier" : "Ajouter"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Variant Dialog */}
      <Dialog open={variantDialogOpen} onOpenChange={setVariantDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Ajouter une variante</DialogTitle>
            <DialogDescription>
              {selectedProduct?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{labels.size} *</Label>
              <Select value={newVariant.size} onValueChange={(value) => setNewVariant({...newVariant, size: value})}>
                <SelectTrigger>
                  <SelectValue placeholder={`Choisir une ${labels.size.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {sizeOptions.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{labels.color} *</Label>
              <Select value={newVariant.color} onValueChange={(value) => {
                const preset = colorOptions.find(c => c.name === value);
                setNewVariant({...newVariant, color: value, color_hex: preset?.hex || "#000000"});
              }}>
                <SelectTrigger>
                  <SelectValue placeholder={`Choisir un ${labels.color.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {colorOptions.map((c) => (
                    <SelectItem key={c.name} value={c.name}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full border" style={{ backgroundColor: c.hex }} />
                        {c.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Quantité en stock</Label>
              <Input
                type="number"
                min="0"
                value={newVariant.quantity}
                onChange={(e) => setNewVariant({...newVariant, quantity: parseInt(e.target.value) || 0})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVariantDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleAddVariant}>Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet article ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. L'article "{productToDelete?.name}" {showVariants ? "et toutes ses variantes seront supprimés" : "sera supprimé"}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={requestDelete} className="bg-destructive hover:bg-destructive/90">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* [DELETION-PIN] Owner PIN required for team member deletion */}
      <DeletionPinDialog
        open={deletionPin.open}
        onClose={deletionPin.cancel}
        onSubmit={(pin) => deletionPin.submitPin(pin).catch((err) => {
          toast.error(err?.response?.data?.detail || "Erreur lors de la suppression");
        })}
        loading={deletionPin.loading}
        error={deletionPin.error}
        title={`Supprimer « ${productToDelete?.name || 'cet article'} »`}
        description="La suppression d'un article requiert le code PIN du propriétaire de la boutique."
      />

      {/* [APPRO-STOCK] Restock & History dialogs */}
      <RestockDialog
        open={restockOpen} onOpenChange={setRestockOpen}
        product={restockProduct} devise={devise}
        getAuthHeaders={getAuthHeaders} onDone={fetchData}
      />
      <StockHistoryDialog
        open={historyOpen} onOpenChange={setHistoryOpen}
        getAuthHeaders={getAuthHeaders} devise={devise}
      />
    </div>
  );
}
